import type { NoteDetail } from '@kartothek/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import { useInvalidateCampaign } from '../hooks';
import { t } from '../i18n';
import { classNames } from '../util';

const date = (iso: string) => new Date(iso).toLocaleString('de-DE', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });

/**
 * Freigeben für den DM: nur DM, alle Spieler oder einzelne – mit Stand je Spieler
 * („neu“ bzw. „gesehen“). Die Rechte prüft der Server; hier wird nur die Sichtbarkeit gesetzt.
 */
export function RevealControl({ note }: { note: NoteDetail }) {
  const cid = note.campaignId;
  const qc = useQueryClient();
  const invalidate = useInvalidateCampaign(cid);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const access = useQuery({ queryKey: ['access', cid, note.id], queryFn: () => api.noteAccess(cid, note.id), enabled: open });
  const players = access.data ?? [];
  const selected = new Set(note.visibility === 'players' ? (note.visiblePlayerIds ?? []) : []);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const apply = async (visibility: NoteDetail['visibility'], ids: string[] | null = null) => {
    setError(null);
    try {
      // Ohne ausgewählte Spieler ist „bestimmte Spieler“ gleichbedeutend mit „nur DM“
      const vis = visibility === 'players' && (!ids || ids.length === 0) ? 'dm' : visibility;
      const visiblePlayerIds = vis === 'players' ? ids : null;
      // Sofort anzeigen; bei Fehler lädt die Notiz unten neu
      qc.setQueryData<NoteDetail>(['note', cid, note.id], (old) => (old ? { ...old, visibility: vis, visiblePlayerIds } : old));
      const res = await api.updateNote(cid, note.id, { visibility: vis, visiblePlayerIds });
      qc.setQueryData(['note', cid, note.id], res);
      void qc.invalidateQueries({ queryKey: ['access', cid, note.id] });
      invalidate();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      void qc.invalidateQueries({ queryKey: ['note', cid, note.id] });
    }
  };

  /** Besitzer darf die Notiz bearbeiten (z. B. seinen Charakter) und sieht sie immer. */
  const setOwner = async (ownerId: string | null) => {
    setError(null);
    try {
      const res = await api.updateNote(cid, note.id, { ownerId });
      qc.setQueryData(['note', cid, note.id], res);
      void qc.invalidateQueries({ queryKey: ['access', cid, note.id] });
      invalidate();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  const toggle = (uid: string) => {
    const next = new Set(selected);
    if (next.has(uid)) next.delete(uid);
    else next.add(uid);
    void apply('players', [...next]);
  };

  const label =
    note.visibility === 'all'
      ? t('visibility.all')
      : note.visibility === 'players'
        ? t('reveal.some', { n: note.visiblePlayerIds?.length ?? 0 })
        : t('visibility.dm');

  return (
    <div className="reveal" ref={ref}>
      <button className={classNames('reveal-btn', `vis-${note.visibility}`)} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        👁 {label}
      </button>
      {open && (
        <div className="reveal-panel card" role="dialog" aria-label={t('reveal.title')}>
          <strong>{t('reveal.title')}</strong>
          <div className="reveal-options">
            <button className={classNames(note.visibility === 'dm' && 'on')} onClick={() => void apply('dm')}>
              {t('visibility.dm')}
            </button>
            <button className={classNames(note.visibility === 'all' && 'on')} onClick={() => void apply('all')}>
              {t('reveal.toAll')}
            </button>
          </div>
          {access.isLoading && <p className="muted small">{t('common.loading')}</p>}
          {access.data && players.length === 0 && <p className="muted small">{t('reveal.noPlayers')}</p>}
          {players.length > 0 && (
            <ul className="reveal-players">
              {players.map((p) => (
                <li key={p.userId}>
                  <label>
                    <input
                      type="checkbox"
                      checked={note.visibility === 'all' || selected.has(p.userId)}
                      disabled={note.visibility === 'all'}
                      onChange={() => toggle(p.userId)}
                    />
                    {p.name}
                  </label>
                  <span className="small muted">
                    {p.canSee && p.seenAt
                      ? t('reveal.seen', { date: date(p.seenAt) })
                      : p.canSee && p.grantedAt
                        ? t('reveal.unseen')
                        : p.canSee
                          ? ''
                          : '—'}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {players.length > 0 && (
            <label className="reveal-owner">
              {t('reveal.owner')}
              <select value={note.ownerId ?? ''} onChange={(e) => void setOwner(e.target.value || null)}>
                <option value="">{t('reveal.noOwner')}</option>
                {players.map((p) => (
                  <option key={p.userId} value={p.userId}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <p className="small muted">{t('reveal.hint')}</p>
          {error && <div className="error small">{error}</div>}
        </div>
      )}
    </div>
  );
}
